/**
 * The phone's rail: five slots in flow at the bottom of the content column —
 * the first four rail entries, then More, which opens a sheet over the page listing
 * everything else (phone shell spec §3.3). Hidden at the nav breakpoint and
 * above, and on the Chat page, whose fixed composer owns the bottom. Inbox's
 * slot carries the unread badge.
 */
import React, { useState } from 'react';

import { useLocation } from 'react-router-dom';

import { useAppStore } from '@/stores/useAppStore';
import { useWorkspaceNav } from '@features/workspace/hooks/useWorkspaceNav';
import { useInstalledApp } from '@shared/app-identity/useInstalledApp';
import { DomainIcon, Icons } from '@shared/components/ui';
import { destinationById, destinationForPath } from '@shared/navigation/destinations';
import { useRail } from '@shared/navigation/useRail';

import { MoreSheet } from './MoreSheet';
import { hasMoreThanSlots } from './moreSheetSections';
import { bottomBarSlots, countForStar } from './starSlots';
import CollapsedNavBadge from '../SideNav/CollapsedNavBadge';

import type { Destination } from '@shared/navigation/destinations';

const SLOT =
  'flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-xs leading-tight';

interface SlotProps {
  d: Destination;
  active: boolean;
  count: number | null;
  onOpen: () => void;
  /** Set for the More slot: it opens a sheet, and says whether that sheet is open. */
  expanded?: boolean;
}

/** One slot: icon (with badge) over a label; the active destination is highlighted. */
const Slot: React.FC<SlotProps> = ({ d, active, count, onOpen, expanded }) => {
  const Glyph = Icons[d.icon];
  const label = count != null && count > 0 ? `${d.label} (${count})` : d.label;
  return (
    <button
      type="button"
      aria-label={label}
      {...(active && { 'aria-current': 'page' as const })}
      {...(expanded !== undefined && { 'aria-expanded': expanded, 'aria-haspopup': 'dialog' as const })}
      onClick={onOpen}
      className={`${SLOT} ${active ? 'text-sidebar-brand' : 'text-sidebar-foreground/80'}`}
    >
      <span className="relative inline-flex">
        {d.domain ? (
          <DomainIcon domain={d.domain} size="md" />
        ) : (
          <Glyph size="md" color="inherit" />
        )}
        {count != null && count > 0 && <CollapsedNavBadge count={count} />}
      </span>
      <span className="max-w-full truncate">{d.label}</span>
    </button>
  );
};

/** The phone's bottom bar: first four stars + More. Absent on `/chat*`. */
const MobileBottomBar: React.FC = () => {
  const { pathname } = useLocation();
  const { ids } = useRail();
  const installedApp = useInstalledApp();
  const counts = useAppStore(s => s.navCounts);
  const { openDestination } = useWorkspaceNav();
  const [moreOpen, setMoreOpen] = useState(false);

  // The Chat page's fixed composer owns the bottom (segment boundary: /chat and /chat/*).
  if (pathname === '/chat' || pathname.startsWith('/chat/')) return null;

  const { slots } = bottomBarSlots(ids);
  const activeId = destinationForPath(pathname)?.id;
  const areas = destinationById('areas');
  const showMore = areas !== undefined && hasMoreThanSlots(ids, installedApp);

  return (
    <nav
      aria-label="Bottom bar"
      className="bg-sidebar text-sidebar-foreground border-border pb-safe nav:hidden flex shrink-0 border-t"
    >
      {slots.map(d => (
        <Slot
          key={d.id}
          d={d}
          active={activeId === d.id}
          count={countForStar(d.id, counts)}
          onOpen={() => openDestination(d.id)}
        />
      ))}
      {showMore && (
        <Slot
          d={{ ...areas, label: 'More' }}
          active={moreOpen}
          count={null}
          expanded={moreOpen}
          onOpen={() => setMoreOpen(true)}
        />
      )}
      {showMore && <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />}
    </nav>
  );
};

export default MobileBottomBar;
